<template>
  <div class="mechanics-list">
    <b-card no-body>
      <b-card-body class="d-flex align-items-center">
        <h4 class="mb-0 mr-1">Механики вопросов</h4>
        <b-form-checkbox v-model="showArchived" class="mr-1">показывать архивные</b-form-checkbox>
        <b-button
          v-if="canManage"
          variant="primary"
          size="sm"
          class="ml-auto"
          @click="openEdit(null)"
        >
          <feather-icon icon="PlusIcon" class="mr-25" />
          Добавить
        </b-button>
      </b-card-body>

      <b-table
        :items="visibleMechanics"
        :fields="tableColumns"
        primary-key="id"
        responsive
        show-empty
        empty-text="Механик нет"
        class="position-relative mb-0"
        :busy="loading"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-50" />
            <strong>Загрузка...</strong>
          </div>
        </template>

        <template #cell(name)="data">
          <div class="font-weight-bold">{{ data.item.name }}</div>
          <small v-if="data.item.description" class="text-muted">{{ data.item.description }}</small>
        </template>

        <template #cell(code)="data">
          <code v-if="data.item.code">{{ data.item.code }}</code>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(content_kind)="data">
          {{ contentKindLabels[data.item.content_kind] || data.item.content_kind }}
        </template>

        <template #cell(slots)="data">
          <span :title="slotsTitle(data.item)">{{ slotsCount(data.item) }}</span>
        </template>

        <template #cell(is_archived)="data">
          <b-badge v-if="data.item.is_archived" variant="light-secondary">Архив</b-badge>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(actions)="data">
          <span class="text-nowrap">
            <b-button
              v-if="canManage"
              variant="flat-primary"
              size="sm"
              class="btn-icon"
              title="Изменить"
              @click="openEdit(data.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <!-- Отключённая кнопка не ловит hover — подсказка на обёртке -->
            <span
              v-if="canManage"
              class="d-inline-block"
              :title="data.item.questions_count > 0 ? 'Есть вопросы — только в архив' : 'Удалить'"
            >
              <b-button
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                :disabled="data.item.questions_count > 0 || deletingId === data.item.id"
                @click="confirmDelete(data.item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </span>
          </span>
        </template>
      </b-table>
    </b-card>

    <mechanic-edit-modal
      v-model="isModalVisible"
      :mechanic="editedMechanic"
      @saved="fetchMechanics"
    />
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BCardBody, BFormCheckbox, BSpinner, BTable,
} from 'bootstrap-vue'
import { computed, getCurrentInstance, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import questionStorageStoreModule from '../questionStorageStoreModule'
import MechanicEditModal from './MechanicEditModal.vue'

const MODULE = 'question-storage'

export default {
  components: {
    BBadge, BButton, BCard, BCardBody, BFormCheckbox, BSpinner, BTable, MechanicEditModal,
  },
  setup(props, { root }) {
    // Модуль стора не снимаем: Vue 2 создаёт компонент следующего роута до уничтожения текущего (см. import/PackImport.vue)
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, questionStorageStoreModule)

    const toast = useToast()
    const instance = getCurrentInstance().proxy
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })

    const canManage = computed(() => instance.$can('manage', 'questions'))

    const contentKindLabels = { music: 'Музыка', movies: 'Кино', any: 'Любой' }
    const tableColumns = [
      { key: 'name', label: 'Название' },
      { key: 'code', label: 'Код' },
      { key: 'content_kind', label: 'Контент' },
      { key: 'slots', label: 'Слотов' },
      { key: 'questions_count', label: 'Вопросов' },
      { key: 'is_archived', label: 'Архив' },
      { key: 'actions', label: '' },
    ]

    const mechanics = ref([])
    const loading = ref(false)
    const showArchived = ref(true)
    const isModalVisible = ref(false)
    const editedMechanic = ref(null)
    const deletingId = ref(null)

    const fetchMechanics = () => {
      loading.value = true
      store.dispatch(`${MODULE}/fetchMechanics`)
        .then(response => { mechanics.value = response.data.data || [] })
        .catch(() => showError('Не удалось загрузить механики'))
        .finally(() => { loading.value = false })
    }
    fetchMechanics()

    const visibleMechanics = computed(() => (showArchived.value
      ? mechanics.value
      : mechanics.value.filter(mechanic => !mechanic.is_archived)))

    const slotsCount = mechanic => (Array.isArray(mechanic.slots) ? mechanic.slots.length : 0)
    const slotsTitle = mechanic => (Array.isArray(mechanic.slots) && mechanic.slots.length
      ? mechanic.slots.map(slot => `${slot.name} (${slot.code})`).join(', ')
      : 'Без слотов — один слот «Ответ»')

    const openEdit = mechanic => {
      editedMechanic.value = mechanic
      isModalVisible.value = true
    }

    const confirmDelete = async mechanic => {
      const confirmed = await root.$bvModal.msgBoxConfirm(`Удалить механику «${mechanic.name}»?`, {
        title: 'Удаление механики',
        okTitle: 'Удалить',
        cancelTitle: 'Отмена',
        okVariant: 'danger',
        centered: true,
      })
      if (!confirmed) return
      deletingId.value = mechanic.id
      store.dispatch(`${MODULE}/deleteMechanic`, mechanic.id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: { title: 'Механика удалена', icon: 'CheckIcon', variant: 'success' },
          })
          fetchMechanics()
        })
        .catch(error => {
          const data = error && error.response && error.response.data
          showError((data && data.message) || 'Не удалось удалить механику')
        })
        .finally(() => { deletingId.value = null })
    }

    return {
      canManage,
      contentKindLabels,
      tableColumns,
      loading,
      showArchived,
      isModalVisible,
      editedMechanic,
      deletingId,
      visibleMechanics,
      fetchMechanics,
      slotsCount,
      slotsTitle,
      openEdit,
      confirmDelete,
    }
  },
}
</script>
